import React, { useState, useRef } from 'react';
import { Label } from '../ui/label';
import { UploadCloud, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wiederverwendbare Drag-&-Drop-Zone für PDF-Uploads.
 *
 * Props:
 *  - file:      aktuell ausgewählte File (oder null)
 *  - onChange:  (file|null) => void
 *  - label:     Überschrift (default: "PDF-Datei hochladen")
 *  - hint:      Untertext (default: gibt Drop/Click-Anleitung)
 *  - testId:    data-testid-Präfix (default: "pdf-dropzone")
 *  - error:     Fehlermeldung (z.B. von einer Validation), wird unter dem Block gerendert
 *  - validate:  optionale Funktion(name) => string|""  (Custom-Validation, z.B. Filename-Check)
 *  - compact:   bool — kleinere Variante für Inline-Nutzung
 *  - accept:    file-input accept-Attribut (default: "application/pdf,.pdf")
 */
export default function PdfDropzone({
    file,
    onChange,
    label = 'PDF-Datei hochladen',
    hint = 'Datei hierhin ziehen oder über den Button auswählen.',
    testId = 'pdf-dropzone',
    error = '',
    validate,
    compact = false,
    accept = 'application/pdf,.pdf',
    multiple = false,
}) {
    const [drag, setDrag] = useState(false);
    const inputRef = useRef(null);

    const handleFiles = (fileList) => {
        if (!fileList || fileList.length === 0) {
            if (multiple) onChange([]);
            else onChange(null);
            return;
        }
        const arr = Array.from(fileList);
        const invalid = arr.find((f) => !f.name.toLowerCase().endsWith('.pdf'));
        if (invalid) {
            toast.error('Nur PDF-Dateien erlaubt');
            return;
        }
        if (validate) {
            for (const f of arr) {
                const msg = validate(f.name);
                if (msg) { toast.error(msg); }
            }
        }
        if (multiple) onChange(arr);
        else onChange(arr[0] || null);
    };

    const handleFile = (f) => handleFiles(f ? [f] : null);

    return (
        <div className="space-y-1">
            <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); }}
                onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    setDrag(false);
                    handleFiles(e.dataTransfer?.files);
                }}
                onClick={() => inputRef.current?.click()}
                className={[
                    'rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                    compact ? 'p-3' : 'p-4',
                    drag ? 'border-primary bg-primary/10' : 'border-primary/30 bg-primary/5 hover:border-primary/50',
                    error ? 'border-rose-400 bg-rose-50' : '',
                ].filter(Boolean).join(' ')}
                data-testid={testId}
            >
                <div className="flex items-center gap-3">
                    <UploadCloud className={`flex-shrink-0 ${compact ? 'h-5 w-5' : 'h-6 w-6'} text-primary/70`} />
                    <div className="flex-1 min-w-0">
                        <Label className={`font-medium cursor-pointer ${compact ? 'text-xs' : 'text-sm'}`}>
                            {label}
                        </Label>
                        <p className={`text-muted-foreground ${compact ? 'text-[11px]' : 'text-xs'}`}>{hint}</p>
                    </div>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                    data-testid={`${testId}-input`}
                />
                {!multiple && file && (
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs bg-white/70 rounded px-2 py-1.5">
                        <span className="flex items-center gap-1.5 text-emerald-700 truncate">
                            <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                            <strong className="truncate">{file.name}</strong>
                            <span className="text-muted-foreground flex-shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
                            className="text-rose-600 hover:text-rose-700 flex-shrink-0"
                            data-testid={`${testId}-clear`}
                            aria-label="Datei entfernen"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>
            {error && (
                <p className="text-xs text-rose-600" data-testid={`${testId}-error`}>{error}</p>
            )}
        </div>
    );
}
